import { useEffect, useMemo, useState } from 'react';

import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import {
  AfterDeath,
  DispositiveProvisionRecipientKind,
  DispositiveProvisionTransferTaxKind,
} from '@/types/schema';
import { diagnostics } from '@/utils/diagnostics';
import { UnreachableError } from '@/utils/errors';
import { getNodes } from '@/utils/graphqlUtils';

import { BdgResolverEntityInput, getBdgTriggerInfo } from '../bdgTriggerInfo';
import { collectDownstreamRecipients } from '../collectDownstreamRecipients';
import { RecipientDispositions } from '../getDispositionsVizFromEntity';
import {
  Operations as CltTermDiagramOperations,
  useCltTermDiagramQuery,
} from '../graphql/CltTermDiagram.generated';
import {
  Operations as CrtTermDiagramOperations,
  useCrtTermDiagramQuery,
} from '../graphql/CrtTermDiagram.generated';
import {
  DispositionsDiagram_DispositionProvisionSecondDeathFragment,
  DispositionsDiagram_EntityFragment,
  Operations as DispositionsDiagramOperations,
  useDispositionsDiagramQuery,
} from '../graphql/DispositionsDiagram.generated';
import {
  Operations as GratTermDiagramOperations,
  useGratTermDiagramQuery,
} from '../graphql/GratTermDiagram.generated';
import {
  Operations as OwnershipDiagramOperations,
  useOwnershipDiagramQuery,
} from '../graphql/OwnershipDiagram.generated';
import {
  Operations as QprtTermDiagramOperations,
  useQprtTermDiagramQuery,
} from '../graphql/QprtTermDiagram.generated';
import { resolveEntityNormalizedDispositions } from '../resolveEntityNormalizedDispositions';
import { EntityDiagramVariant } from '../types';

// Given an entity diagram variant and a query name, determine if we should skip the query
// because it is not needed for the given variant.
function shouldSkipQuery(
  entityDiagramVariant: EntityDiagramVariant | null,
  operationName:
    | typeof DispositionsDiagramOperations.Query.DispositionsDiagram
    | typeof OwnershipDiagramOperations.Query.OwnershipDiagram
    | typeof GratTermDiagramOperations.Query.GratTermDiagram
    | typeof QprtTermDiagramOperations.Query.QprtTermDiagram
    | typeof CrtTermDiagramOperations.Query.CrtTermDiagram
    | typeof CltTermDiagramOperations.Query.CltTermDiagram,
  selectedPrimaryClientId?: string
) {
  if (selectedPrimaryClientId === '') {
    return true;
  }

  if (
    entityDiagramVariant === EntityDiagramVariant.Dispositions &&
    operationName === DispositionsDiagramOperations.Query.DispositionsDiagram
  ) {
    return false;
  }

  if (
    entityDiagramVariant === EntityDiagramVariant.Ownership &&
    operationName === OwnershipDiagramOperations.Query.OwnershipDiagram
  ) {
    return false;
  }

  if (
    entityDiagramVariant === EntityDiagramVariant.GratTerm &&
    operationName === GratTermDiagramOperations.Query.GratTermDiagram
  ) {
    return false;
  }

  if (
    entityDiagramVariant === EntityDiagramVariant.QprtTerm &&
    operationName === QprtTermDiagramOperations.Query.QprtTermDiagram
  ) {
    return false;
  }

  if (
    entityDiagramVariant === EntityDiagramVariant.CrtTerm &&
    operationName === CrtTermDiagramOperations.Query.CrtTermDiagram
  ) {
    return false;
  }

  if (
    entityDiagramVariant === EntityDiagramVariant.CltTerm &&
    operationName === CltTermDiagramOperations.Query.CltTermDiagram
  ) {
    return false;
  }

  return true;
}

/**
 * Collects the entity ids that the root (or a possible primary client) pours
 * over into. A pour-over is a non-tier-consuming hop, so the depth-2-capped
 * root query stops one level short below it. These ids seed the look-through
 * query that fetches that missing level.
 */
function collectPouroverTargetIds(
  rootEntity: DispositionsDiagram_EntityFragment,
  selectedPrimaryClientId: string,
  primaryClients: { id: string }[],
  fetchedTargets: DispositionsDiagram_EntityFragment[] = []
): string[] {
  const ids = new Set<string>();
  const visited = new Set<string>();
  const targetsById = new Map(
    fetchedTargets.map((target) => [target.id, target])
  );
  const visit = (
    inlineEntity: BdgResolverEntityInput,
    section: AfterDeath,
    receivedViaPourover = false,
    isSecondPhase = false
  ): void => {
    const entity = targetsById.get(inlineEntity.id) ?? inlineEntity;
    const resolved = resolveEntityNormalizedDispositions(entity, {
      firstGrantorDeathId: selectedPrimaryClientId,
      possiblePrimaryClients: primaryClients,
      placedAt: section,
    });
    if (!resolved) return;
    const usesFirstDeath =
      !isSecondPhase &&
      (section === AfterDeath.None ||
        (receivedViaPourover && resolved.firstDeath.length > 0));
    const key = `${entity.id}:${section}:${usesFirstDeath ? 'first' : 'second'}:${receivedViaPourover}:${isSecondPhase}`;
    if (visited.has(key)) return;
    visited.add(key);
    // Match the renderer's terminal-node rule. An ordinary final-section
    // recipient only distributes if its second schedule contains a pour-over.
    if (
      section === AfterDeath.Second &&
      !receivedViaPourover &&
      !isSecondPhase &&
      !resolved.secondDeath.some(
        (dp) =>
          dp.transferTaxKind === DispositiveProvisionTransferTaxKind.Pourover
      )
    )
      return;
    const provisions = usesFirstDeath
      ? resolved.firstDeath
      : resolved.secondDeath;
    provisions.forEach((dp) => {
      const isPourover =
        dp.transferTaxKind === DispositiveProvisionTransferTaxKind.Pourover;
      let recipientSection = section;
      if (!isPourover) {
        recipientSection =
          section === AfterDeath.None ? AfterDeath.First : AfterDeath.Second;
      }
      if (isPourover && dp.entity?.id) ids.add(dp.entity.id);
      if (dp.entity) {
        // Terminal fragments keep the resolver fields when they are present;
        // a fetched target supplies the deeper fields on later query rounds.
        visit(
          dp.entity as unknown as BdgResolverEntityInput,
          recipientSection,
          isPourover
        );
      } else {
        let carrier = dp.testamentaryEntity ?? dp.individual;
        if (
          dp.recipientKind === DispositiveProvisionRecipientKind.SurvivingSpouse
        ) {
          carrier =
            rootEntity.household?.possiblePrimaryClients?.find(
              (client) => client.id !== selectedPrimaryClientId
            ) ?? null;
        }
        // Only non-terminal carriers expose another level of scenarios.
        if (carrier && 'dispositionScenarios' in carrier) {
          visit(
            {
              id: carrier.id,
              subtype: { dispositionScenarios: carrier.dispositionScenarios },
            } as unknown as BdgResolverEntityInput,
            recipientSection,
            isPourover
          );
        }
      }
    });
    // A pour-over can start its own two-phase sequence in a later section.
    if (
      usesFirstDeath &&
      resolved.secondDeath.length > 0 &&
      (section !== AfterDeath.First || primaryClients.length > 1)
    ) {
      visit(
        entity,
        section === AfterDeath.None ? AfterDeath.First : AfterDeath.Second,
        false,
        true
      );
    }
  };

  visit(rootEntity, AfterDeath.None);
  rootEntity.household?.possiblePrimaryClients?.forEach((client) => {
    visit(
      {
        id: client.id,
        subtype: { dispositionScenarios: client.dispositionScenarios },
      } as unknown as BdgResolverEntityInput,
      AfterDeath.None
    );
  });
  return Array.from(ids);
}

interface UseQueryEntityDiagramProps {
  entityId: string;
  entityDiagramVariant: EntityDiagramVariant | null;
  selectedPrimaryClientId?: string;
  /**
   * Pass the waterfall id when mounted in a hypothetical context to read
   * hypothetical-scope BDGs (server falls back to live values).
   */
  hypotheticalWaterfallId?: string | null;
}

export function useQueryEntityDiagram({
  entityId,
  entityDiagramVariant,
  selectedPrimaryClientId,
  hypotheticalWaterfallId = null,
}: UseQueryEntityDiagramProps) {
  const { primaryClients } = useHouseholdDetailsContext();
  const { showFeedback } = useFeedback();

  const {
    data: dispositionsData,
    loading: dispositionsLoading,
    refetch: refetchDispositions,
  } = useDispositionsDiagramQuery({
    fetchPolicy: 'no-cache',
    // Report loading on refetch (not just initial load) so the pour-over gate
    // below can also wait for a concurrent root refresh, not just the
    // look-through — otherwise fresh depth-3 data can paint over a stale root.
    notifyOnNetworkStatusChange: true,
    variables: {
      entityWhere: { id: entityId },
      hypotheticalWaterfallId,
      // Force refetch when death order changes
      _forceRefetchKey: selectedPrimaryClientId,
    },
    onError: (error) => {
      showFeedback(
        `Sorry, we failed to load the page. Please refresh and try again.`
      );
      diagnostics.error('dispositions diagram load failure', error, {
        entityId,
      });
    },
    skip: shouldSkipQuery(
      entityDiagramVariant,
      DispositionsDiagramOperations.Query.DispositionsDiagram,
      selectedPrimaryClientId
    ),
  });

  // Pour-over targets are non-tier-consuming hops, so the depth-2-capped root
  // query never reaches the recipients of the target's recipients. Detect
  // those targets so we can fetch one level deeper below them.
  const initialPourOverTargetIds = useMemo(() => {
    if (entityDiagramVariant !== EntityDiagramVariant.Dispositions) return [];
    if (!dispositionsData || !selectedPrimaryClientId) return [];
    const rootEntity = getNodes(dispositionsData.entities)[0];
    // Ignore stale data after navigation: a root entity that doesn't match
    // the requested entityId would otherwise fire the look-through for the
    // previous page's trusts.
    if (!rootEntity || rootEntity.id !== entityId) return [];
    return collectPouroverTargetIds(
      rootEntity,
      selectedPrimaryClientId,
      primaryClients ?? []
    );
  }, [
    dispositionsData,
    entityDiagramVariant,
    entityId,
    selectedPrimaryClientId,
    primaryClients,
  ]);
  const pourOverScope = JSON.stringify([
    entityId,
    selectedPrimaryClientId,
    hypotheticalWaterfallId,
    initialPourOverTargetIds,
  ]);
  const [discoveredPourOverTargets, setDiscoveredPourOverTargets] = useState<{
    scope: string;
    ids: string[];
  }>({
    scope: pourOverScope,
    ids: [],
  });
  const pourOverTargetIds = useMemo(() => {
    if (initialPourOverTargetIds.length === 0) return [];
    return Array.from(
      new Set([
        ...initialPourOverTargetIds,
        ...(discoveredPourOverTargets.scope === pourOverScope
          ? discoveredPourOverTargets.ids
          : []),
      ])
    );
  }, [initialPourOverTargetIds, discoveredPourOverTargets, pourOverScope]);

  // Look-through query: re-roots DispositionsDiagram at each pour-over target.
  // Re-rooted, the target's recipients (depth-2 from the original root) carry
  // their own scenarios, supplying the depth-3 dispositions the cascade needs.
  // Skipped entirely when there is no pour-over.
  const {
    data: pouroverLookThroughData,
    loading: pouroverLookThroughLoading,
    refetch: refetchPouroverLookThrough,
  } = useDispositionsDiagramQuery({
    fetchPolicy: 'no-cache',
    // Surface loading on refetch (not just initial load) so the
    // isAwaitingPouroverLookThrough gate also covers a post-save refresh;
    // otherwise the manual refetch would silently swap stale depth-3 data.
    notifyOnNetworkStatusChange: true,
    variables: {
      entityWhere: { idIn: pourOverTargetIds },
      hypotheticalWaterfallId,
      // Force refetch when death order changes
      _forceRefetchKey: selectedPrimaryClientId,
    },
    onError: (error) => {
      showFeedback(
        `Sorry, we failed to load the page. Please refresh and try again.`
      );
      diagnostics.error('pourover look-through diagram load failure', error, {
        entityId,
      });
    },
    skip:
      shouldSkipQuery(
        entityDiagramVariant,
        DispositionsDiagramOperations.Query.DispositionsDiagram,
        selectedPrimaryClientId
      ) || pourOverTargetIds.length === 0,
  });
  const pendingPourOverTargetIds = useMemo(() => {
    if (
      !selectedPrimaryClientId ||
      dispositionsLoading ||
      pouroverLookThroughLoading
    )
      return [];
    const rootEntity = getNodes(dispositionsData?.entities)[0];
    if (!rootEntity || rootEntity.id !== entityId) return [];
    // Traverse from the original root so fetched targets keep their incoming
    // placement and phase. Re-rooting every target at None scans unused phases.
    const ids = collectPouroverTargetIds(
      rootEntity,
      selectedPrimaryClientId,
      primaryClients ?? [],
      getNodes(pouroverLookThroughData?.entities).filter((target) =>
        pourOverTargetIds.includes(target.id)
      )
    );
    return ids.filter((id) => !pourOverTargetIds.includes(id));
  }, [
    dispositionsLoading,
    pouroverLookThroughLoading,
    pouroverLookThroughData,
    pourOverTargetIds,
    selectedPrimaryClientId,
    dispositionsData,
    entityId,
    primaryClients,
  ]);
  useEffect(() => {
    if (pendingPourOverTargetIds.length === 0) return;
    setDiscoveredPourOverTargets((previous) => ({
      scope: pourOverScope,
      ids: Array.from(
        new Set([
          ...(previous.scope === pourOverScope ? previous.ids : []),
          ...pendingPourOverTargetIds,
        ])
      ),
    }));
  }, [pendingPourOverTargetIds, pourOverScope]);

  const { data: ownershipData, ...ownershipRest } = useOwnershipDiagramQuery({
    fetchPolicy: 'no-cache',
    variables: {
      entityWhere: {
        id: entityId,
      },
    },
    onError: (error) => {
      showFeedback(
        `Sorry, we failed to load the page. Please refresh and try again.`
      );
      diagnostics.error('ownership diagram load failure', error, {
        entityId,
      });
    },
    skip: shouldSkipQuery(
      entityDiagramVariant,
      OwnershipDiagramOperations.Query.OwnershipDiagram
    ),
  });

  const { data: gratTermData, ...gratTermRest } = useGratTermDiagramQuery({
    fetchPolicy: 'no-cache',
    variables: {
      entityWhere: {
        id: entityId,
      },
    },
    onError: (error) => {
      showFeedback(
        `Sorry, we failed to load the page. Please refresh and try again.`
      );
      diagnostics.error('GRAT term diagram load failure', error, {
        entityId,
      });
    },
    skip: shouldSkipQuery(
      entityDiagramVariant,
      GratTermDiagramOperations.Query.GratTermDiagram
    ),
  });

  const { data: qprtTermData, ...qprtTermRest } = useQprtTermDiagramQuery({
    fetchPolicy: 'no-cache',
    variables: {
      entityWhere: {
        id: entityId,
      },
    },
    onError: (error) => {
      showFeedback(
        `Sorry, we failed to load the page. Please refresh and try again.`
      );
      diagnostics.error('QPRT term diagram load failure', error, {
        entityId,
      });
    },
    skip: shouldSkipQuery(
      entityDiagramVariant,
      QprtTermDiagramOperations.Query.QprtTermDiagram
    ),
  });

  const { data: crtTermData, ...crtTermRest } = useCrtTermDiagramQuery({
    fetchPolicy: 'no-cache',
    variables: {
      entityWhere: {
        id: entityId,
      },
    },
    onError: (error) => {
      showFeedback(
        `Sorry, we failed to load the page. Please refresh and try again.`
      );
      diagnostics.error('CRT term diagram load failure', error, {
        entityId,
      });
    },
    skip: shouldSkipQuery(
      entityDiagramVariant,
      CrtTermDiagramOperations.Query.CrtTermDiagram
    ),
  });

  const { data: cltTermData, ...cltTermRest } = useCltTermDiagramQuery({
    fetchPolicy: 'no-cache',
    variables: {
      entityWhere: {
        id: entityId,
      },
    },
    onError: (error) => {
      showFeedback(
        `Sorry, we failed to load the page. Please refresh and try again.`
      );
      diagnostics.error('CLT term diagram load failure', error, {
        entityId,
      });
    },
    skip: shouldSkipQuery(
      entityDiagramVariant,
      CltTermDiagramOperations.Query.CltTermDiagram
    ),
  });

  const refetch = useMemo(() => {
    if (!entityDiagramVariant) {
      return undefined;
    }

    switch (entityDiagramVariant) {
      case EntityDiagramVariant.Dispositions:
        // Depth-3 pour-over data comes from the separate look-through query, so
        // a root refetch (e.g. after an edit-trowser save) must refresh it too;
        // unchanged target ids would otherwise leave the look-through cached.
        return (variables?: Parameters<typeof refetchDispositions>[0]) => {
          if (pourOverTargetIds.length > 0) {
            void refetchPouroverLookThrough();
          }
          return refetchDispositions(variables);
        };
      case EntityDiagramVariant.Ownership:
        return ownershipRest.refetch;
      case EntityDiagramVariant.GratTerm:
        return gratTermRest.refetch;
      case EntityDiagramVariant.CrtTerm:
        return crtTermRest.refetch;
      case EntityDiagramVariant.CltTerm:
        return cltTermRest.refetch;
      case EntityDiagramVariant.QprtTerm:
        return qprtTermRest.refetch;
      default:
        throw new UnreachableError({
          case: entityDiagramVariant,
          message: 'Unhandled entity diagram variant',
        });
    }
  }, [
    entityDiagramVariant,
    refetchDispositions,
    ownershipRest.refetch,
    gratTermRest.refetch,
    crtTermRest.refetch,
    cltTermRest.refetch,
    qprtTermRest.refetch,
    pourOverTargetIds,
    refetchPouroverLookThrough,
  ]);

  const { entity, entityDiagram } = useMemo(() => {
    if (!entityDiagramVariant) {
      return { entity: null, entityDiagram: null };
    }

    switch (entityDiagramVariant) {
      case EntityDiagramVariant.Dispositions: {
        if (!dispositionsData) {
          return { entity: null, entityDiagram: null };
        }
        const entity = getNodes(dispositionsData.entities)[0] ?? null;
        const entityDiagram = selectedPrimaryClientId
          ? (getNodes(entity?.entityDiagrams).find(
              (d) => d?.firstGrantorDeath?.id === selectedPrimaryClientId
            ) ?? null)
          : null;
        return { entity, entityDiagram };
      }
      case EntityDiagramVariant.Ownership: {
        if (!ownershipData) {
          return { entity: null, entityDiagram: null };
        }

        const entity = getNodes(ownershipData.entities)[0] ?? null;
        const entityDiagram = entity?.entityDiagrams?.edges?.[0]?.node ?? null;
        return { entity, entityDiagram };
      }
      case EntityDiagramVariant.GratTerm: {
        if (!gratTermData) {
          return { entity: null, entityDiagram: null };
        }

        const entity = getNodes(gratTermData.entities)[0] ?? null;
        const entityDiagram = entity?.entityDiagrams?.edges?.[0]?.node ?? null;
        return { entity, entityDiagram };
      }
      case EntityDiagramVariant.CrtTerm: {
        if (!crtTermData) {
          return { entity: null, entityDiagram: null };
        }

        const entity = getNodes(crtTermData.entities)[0] ?? null;
        const entityDiagram = entity?.entityDiagrams?.edges?.[0]?.node ?? null;
        return { entity, entityDiagram };
      }
      case EntityDiagramVariant.CltTerm: {
        if (!cltTermData) {
          return { entity: null, entityDiagram: null };
        }

        const entity = getNodes(cltTermData.entities)[0] ?? null;
        const entityDiagram = entity?.entityDiagrams?.edges?.[0]?.node ?? null;
        return { entity, entityDiagram };
      }
      case EntityDiagramVariant.QprtTerm: {
        if (!qprtTermData) {
          return { entity: null, entityDiagram: null };
        }

        const entity = getNodes(qprtTermData.entities)[0] ?? null;
        const entityDiagram = entity?.entityDiagrams?.edges?.[0]?.node ?? null;
        return { entity, entityDiagram };
      }
      default:
        throw new UnreachableError({
          case: entityDiagramVariant,
          message: 'Unhandled entity diagram variant',
        });
    }
  }, [
    cltTermData,
    crtTermData,
    dispositionsData,
    entityDiagramVariant,
    gratTermData,
    ownershipData,
    qprtTermData,
    selectedPrimaryClientId,
  ]);

  // Extract household primary clients' dispositions for disposition diagrams
  const primaryClientRecipientDispositions = useMemo(() => {
    if (
      entityDiagramVariant !== EntityDiagramVariant.Dispositions ||
      !dispositionsData ||
      !selectedPrimaryClientId
    ) {
      return undefined;
    }

    const entity = getNodes(dispositionsData.entities)[0];
    if (!entity?.household?.possiblePrimaryClients) {
      return undefined;
    }

    const dispositionsMap: RecipientDispositions = {};

    // Process each primary client's disposition scenarios
    entity.household.possiblePrimaryClients.forEach((client) => {
      if (!client.dispositionScenarios) return;

      const relevantScenario = client.dispositionScenarios.find(
        (scenario) => scenario.firstGrantorDeath?.id === selectedPrimaryClientId
      );

      if (relevantScenario) {
        const firstDeathDispositions: DispositionsDiagram_DispositionProvisionSecondDeathFragment[] =
          getNodes(
            relevantScenario.dispositiveProvisionsTemplate
              ?.dispositiveProvisions ?? relevantScenario.dispositiveProvisions
          );

        const secondDeathDispositions: DispositionsDiagram_DispositionProvisionSecondDeathFragment[] =
          getNodes(
            relevantScenario.secondDeathDispositiveProvisionsTemplate
              ?.dispositiveProvisions ??
              relevantScenario.secondDeathDispositiveProvisions
          );

        dispositionsMap[client.id] = {
          firstDeath: firstDeathDispositions,
          secondDeath: secondDeathDispositions,
        };
      }
    });

    return Object.keys(dispositionsMap).length > 0
      ? dispositionsMap
      : undefined;
  }, [dispositionsData, entityDiagramVariant, selectedPrimaryClientId]);

  const downstreamRecipientDispositions = useMemo(() => {
    if (
      entityDiagramVariant !== EntityDiagramVariant.Dispositions ||
      !dispositionsData ||
      !selectedPrimaryClientId
    ) {
      return undefined;
    }
    const rootEntity = getNodes(dispositionsData.entities)[0];
    if (!rootEntity) return undefined;

    const map: RecipientDispositions = {};
    collectDownstreamRecipients(rootEntity).forEach((rawEntity) => {
      const resolved = resolveEntityNormalizedDispositions(rawEntity, {
        firstGrantorDeathId: selectedPrimaryClientId,
        possiblePrimaryClients: primaryClients ?? [],
        placedAt: AfterDeath.First,
      });
      if (resolved) map[rawEntity.id] = resolved;
    });
    return Object.keys(map).length > 0 ? map : undefined;
  }, [
    dispositionsData,
    entityDiagramVariant,
    primaryClients,
    selectedPrimaryClientId,
  ]);

  // Recipients of each pour-over target's recipients (depth-3 from the root).
  // Resolving the look-through target's downstream recipients yields a map
  // keyed by the depth-2 recipient id, which the cascade reads to render the
  // next level below a pour-over.
  const pouroverDownstreamRecipientDispositions = useMemo(() => {
    if (
      entityDiagramVariant !== EntityDiagramVariant.Dispositions ||
      // No targets means the look-through query is skipped; ignore any data
      // Apollo may still hold from a prior pour-over entity so depth-3 entries
      // don't linger after the pour-over is removed or the entity changes.
      pourOverTargetIds.length === 0 ||
      !pouroverLookThroughData ||
      !selectedPrimaryClientId
    ) {
      return undefined;
    }
    const map: RecipientDispositions = {};
    getNodes(pouroverLookThroughData.entities).forEach((targetEntity) => {
      if (!pourOverTargetIds.includes(targetEntity.id)) return;
      const targetDispositions = resolveEntityNormalizedDispositions(
        targetEntity,
        {
          firstGrantorDeathId: selectedPrimaryClientId,
          possiblePrimaryClients: primaryClients ?? [],
          placedAt: AfterDeath.First,
        }
      );
      if (targetDispositions) map[targetEntity.id] = targetDispositions;
      // Only the target's own downstream recipients are depth-3 below the
      // pour-over; the household's primary-client scenarios are already
      // resolved at the root level (see downstreamRecipientDispositions).
      collectDownstreamRecipients(targetEntity, {
        includePrimaryClientScenarios: false,
      }).forEach((rawEntity) => {
        const resolved = resolveEntityNormalizedDispositions(rawEntity, {
          firstGrantorDeathId: selectedPrimaryClientId,
          possiblePrimaryClients: primaryClients ?? [],
          placedAt: AfterDeath.First,
        });
        // A re-rooted target query is more complete than its inline fragment.
        if (resolved && !pourOverTargetIds.includes(rawEntity.id))
          map[rawEntity.id] = resolved;
      });
    });
    return Object.keys(map).length > 0 ? map : undefined;
  }, [
    entityDiagramVariant,
    pourOverTargetIds,
    pouroverLookThroughData,
    primaryClients,
    selectedPrimaryClientId,
  ]);

  const rootRecipientDispositions = useMemo(() => {
    if (!dispositionsData || !selectedPrimaryClientId) return undefined;
    const rootEntity = getNodes(dispositionsData.entities)[0];
    if (!rootEntity) return undefined;
    const resolved = resolveEntityNormalizedDispositions(rootEntity, {
      firstGrantorDeathId: selectedPrimaryClientId,
      possiblePrimaryClients: primaryClients ?? [],
      placedAt: AfterDeath.None,
    });
    return resolved ? { [rootEntity.id]: resolved } : undefined;
  }, [dispositionsData, primaryClients, selectedPrimaryClientId]);

  const recipientDispositions = useMemo(() => {
    if (
      !rootRecipientDispositions &&
      !primaryClientRecipientDispositions &&
      !downstreamRecipientDispositions &&
      !pouroverDownstreamRecipientDispositions
    ) {
      return undefined;
    }
    return {
      ...(rootRecipientDispositions ?? {}),
      ...(primaryClientRecipientDispositions ?? {}),
      ...(downstreamRecipientDispositions ?? {}),
      ...(pouroverDownstreamRecipientDispositions ?? {}),
    };
  }, [
    rootRecipientDispositions,
    primaryClientRecipientDispositions,
    downstreamRecipientDispositions,
    pouroverDownstreamRecipientDispositions,
  ]);

  // Null when this isn't a BDG-aware entity (non-Dispositions variant,
  // non-retirement/insurance subtype, or entity not yet loaded).
  const bdgTriggerInfo = useMemo(() => {
    if (entityDiagramVariant !== EntityDiagramVariant.Dispositions) return null;
    if (!dispositionsData) return null;
    const rawEntity = getNodes(dispositionsData.entities)[0] ?? null;
    if (!rawEntity) return null;
    return getBdgTriggerInfo(rawEntity, primaryClients ?? []);
  }, [dispositionsData, entityDiagramVariant, primaryClients]);

  return {
    primaryClients,
    entity,
    entityDiagram,
    recipientDispositions,
    refetch,
    bdgDefaultTriggerClientId: bdgTriggerInfo?.defaultTriggerClientId ?? null,
    bdgIsSurvivorship: bdgTriggerInfo?.isSurvivorship ?? false,
    bdgTriggeringClientIds: bdgTriggerInfo?.triggeringClientIds ?? [],
    isBdgEntity: bdgTriggerInfo !== null,
    // True while a pour-over entity's dispositions data is still settling —
    // a query is in flight or another target is waiting to be queried.
    // Consumers gate rendering on this so the diagram doesn't flash one level
    // too shallow, nor graft fresh depth-3 recipients onto a stale root tree.
    isAwaitingPouroverLookThrough:
      pourOverTargetIds.length > 0 &&
      (pouroverLookThroughLoading ||
        dispositionsLoading ||
        pendingPourOverTargetIds.length > 0),
  };
}
